/**
 * Mojo Up AI Cloud sign-in for the console (docs/plans/2026-10-mojo-up-cloud.md, "Sign-in flows").
 *
 * The console is an OAuth client of Cloud: it makes a PKCE pair (S256, Web Crypto) and a random
 * state, the person proves who they are with a provider or an emailed code, and the one-time code
 * that comes back is exchanged at `/auth/v1/token` with the verifier only this tab holds. Tokens
 * live in sessionStorage. The access token is renewed shortly before it expires or after a 401;
 * renewals are single-flight because a refresh token rotates and spending one twice ends the
 * whole sign-in. A failed renewal signs the person out once, however many requests were waiting.
 *
 * Tabs share one sign-in. sessionStorage is per tab, so a new tab (remote control, a link opened
 * with a middle click) asks the others over a BroadcastChannel and takes the session they hold.
 * Because every tab then holds the same refresh token, a renewal runs under a Web Lock across tabs:
 * inside it a tab first asks the others for anything newer, renews only if nobody already has, and
 * tells every tab the new tokens. Signing out in one tab signs out all of them.
 */

import type { CloudTokenResponse, SignInProvider } from '@aioe/shared';

import { API_URL } from '../api/client.ts';

const SESSION_KEY = 'aioe.cloud.session';
const PENDING_KEY = 'aioe.cloud.pending';
/** Renew this long before the access token runs out, so a request never leaves with a dying one. */
const RENEW_BEFORE_MS = 60_000;

interface Session {
  access: string;
  refresh: string;
  /** Epoch ms. */
  expiresAt: number;
}

/** One sign-in in flight: what the return trip is checked against. */
interface Pending {
  state: string;
  verifier: string;
  /** Where the person was going: back there after signing in. */
  returnTo: string;
  provider: SignInProvider;
  email?: string;
  /** Adding a sign-in method to the signed-in person: the exchange carries this tab's bearer, which alone finishes it. */
  link?: boolean;
  /** The provider let the person in without vouching for their address: the ticket that an emailed code turns into the sign-in. */
  confirm?: string;
  createdAt: number;
}

/** The provider's round trip ended in "confirm your email" (cloudAuth.ts): the callback page asks for the code. */
export class ConfirmEmailNeeded extends Error {
  constructor(
    readonly provider: SignInProvider,
    readonly suggested?: string,
  ) {
    super('Confirm your email address to finish signing in.');
  }
}

export const SIGN_IN_EXPIRED = 'That sign-in expired; try again.';
const CODE_NOT_RIGHT = 'That code is not right.';
/** Sign-up refusals Cloud words itself (cloud/abuse.ts): their `error_description` is shown as it is. */
const SIGN_UP_REFUSALS = new Set(['captcha_required', 'captcha_failed', 'email_domain_blocked', 'sign_up_limit', 'sso_required']);
/** The same refusals coming back from a provider's round trip, as `error_description` codes. */
const SIGN_UP_REFUSAL_TEXT: Record<string, string> = {
  email_domain_blocked: 'AI Cloud does not accept addresses from that email service. Use your work or personal address.',
  sign_up_limit: 'Too many new accounts have been made from here recently. Try again later, or sign in with an account you already have.',
};
const ALREADY_LINKED = 'That account already signs in to another AI Cloud account, so it cannot be added to this one.';

export const redirectUri = () => `${location.origin}/auth/callback`;

function read<T>(key: string): T | undefined {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
}
function write(key: string, value: unknown): void {
  try {
    if (value === undefined) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable: the sign-in lasts as long as the page */
  }
}

let session: Session | undefined = read<Session>(SESSION_KEY);
let refreshing: Promise<string | undefined> | undefined;
let lastRenewal = 0;
const listeners = new Set<() => void>();
const notify = () => {
  for (const l of listeners) l();
};

/** What tabs say to each other: "who has a session?", "here it is" and "signed out". */
type Message = { type: 'ask' } | { type: 'session'; session: Session } | { type: 'end' };
const channel: BroadcastChannel | undefined = typeof BroadcastChannel === 'function' ? new BroadcastChannel('aioe.cloud.session') : undefined;
const tell = (m: Message) => {
  try {
    channel?.postMessage(m);
  } catch {
    /* the channel is closed: this tab carries on alone */
  }
};

/** Takes a session another tab holds, when it is newer than this tab's. */
function adopt(next: Session): void {
  if (session && session.expiresAt >= next.expiresAt) return;
  session = next;
  write(SESSION_KEY, session);
  notify();
}

channel?.addEventListener('message', (e: MessageEvent<Message>) => {
  const m = e.data;
  if (m?.type === 'ask' && session) tell({ type: 'session', session });
  else if (m?.type === 'session' && m.session) adopt(m.session);
  else if (m?.type === 'end') end(false);
});

/** Asks the other tabs for their session and gives them a moment to answer. */
async function askOthers(waitMs: number): Promise<void> {
  if (!channel) return;
  tell({ type: 'ask' });
  await new Promise((r) => setTimeout(r, waitMs));
}

/** A tab opened without a session asks the others before it calls itself signed out. */
const settled: Promise<void> = session ? Promise.resolve() : askOthers(250);

function keep(tokens: CloudTokenResponse): void {
  session = { access: tokens.access_token, refresh: tokens.refresh_token, expiresAt: Date.now() + tokens.expires_in * 1000 };
  write(SESSION_KEY, session);
  tell({ type: 'session', session });
  notify();
}

/** Ends the session here (not on the server), and in every other tab unless `others` is false. Listeners hear it once. */
function end(others = true): void {
  if (!session) return;
  session = undefined;
  write(SESSION_KEY, undefined);
  if (others) tell({ type: 'end' });
  notify();
}

/** Runs `fn` while no other tab renews: the Web Lock where the browser has one, or straight away. */
function exclusively<T>(fn: () => Promise<T>): Promise<T> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  return locks ? (locks.request('aioe.cloud.renew', fn) as Promise<T>) : fn();
}

async function renew(): Promise<string | undefined> {
  return exclusively(async () => {
    const before = session;
    // Another tab may have renewed while this one waited for the lock, or before it woke.
    await askOthers(120);
    if (session && before && session.refresh !== before.refresh && session.expiresAt - RENEW_BEFORE_MS > Date.now()) {
      lastRenewal = Date.now();
      return session.access;
    }
    return renewNow();
  });
}

async function renewNow(): Promise<string | undefined> {
  const s = session;
  if (!s) return undefined;
  let res: Response;
  try {
    res = await fetch(`${API_URL}/auth/v1/token`, { method: 'POST', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify({ grant_type: 'refresh_token', refresh_token: s.refresh }) });
  } catch {
    // The network, not the sign-in: keep the session and let the request fail plainly.
    throw new Error('Mojo Up AI Cloud cannot be reached. Check the connection and try again.');
  }
  if (!res.ok) {
    end();
    throw new Error('Your sign-in has expired: sign in again.');
  }
  keep((await res.json()) as CloudTokenResponse);
  lastRenewal = Date.now();
  return session?.access;
}

export const cloudSession = {
  signedIn: (): boolean => Boolean(session),
  /** Resolves once a new tab has asked the others for their session. */
  settled: (): Promise<void> => settled,
  subscribe(l: () => void): () => void {
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  },
  /** The bearer for the API client. `forceRefresh` after a 401; a renewal a moment ago already answers that. */
  async token(opts?: { forceRefresh?: boolean }): Promise<string | undefined> {
    const s = session;
    if (!s) return undefined;
    const fresh = s.expiresAt - RENEW_BEFORE_MS > Date.now();
    if (fresh && (!opts?.forceRefresh || Date.now() - lastRenewal < 5_000)) return s.access;
    refreshing ??= renew().finally(() => {
      refreshing = undefined;
    });
    return refreshing;
  },
  /** Forgets the session here only: after the account was deleted, the server has already ended it. */
  forget(): void {
    end();
    write(PENDING_KEY, undefined);
  },
  /** Signs out on the server (the whole token family) and here. */
  async signOut(): Promise<void> {
    const s = session;
    end();
    write(PENDING_KEY, undefined);
    if (s) await fetch(`${API_URL}/auth/v1/logout`, { method: 'POST', headers: { authorization: `Bearer ${s.access}` } }).catch(() => undefined);
  },
};

// ---- PKCE ------------------------------------------------------------------------------------

function base64url(bytes: Uint8Array): string {
  let text = '';
  for (const b of bytes) text += String.fromCharCode(b);
  return btoa(text).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
const random = (n: number) => base64url(crypto.getRandomValues(new Uint8Array(n)));

async function begin(provider: SignInProvider, returnTo: string, email?: string, link?: boolean): Promise<{ pending: Pending; challenge: string }> {
  const verifier = random(48); // 64 characters, inside RFC 7636's 43..128
  const challenge = base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
  const pending: Pending = { state: random(24), verifier, returnTo, provider, ...(email ? { email } : {}), ...(link ? { link: true } : {}), createdAt: Date.now() };
  write(PENDING_KEY, pending);
  return { pending, challenge };
}

/** Only paths on this console: a `returnTo` is never another site. */
export function safeReturn(path: string | undefined): string {
  return path && path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/auth/') ? path : '/';
}

async function exchange(code: string, pending: Pending): Promise<string> {
  // Adding a sign-in method is finished only by the sign-in that asked for it: this tab's bearer goes with the code.
  const bearer = pending.link ? await cloudSession.token().catch(() => undefined) : undefined;
  if (pending.link && !bearer) throw new Error(SIGN_IN_EXPIRED);
  const res = await fetch(`${API_URL}/auth/v1/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json', ...(bearer ? { authorization: `Bearer ${bearer}` } : {}) },
    body: JSON.stringify({ grant_type: 'authorization_code', code, code_verifier: pending.verifier, redirect_uri: redirectUri() }),
  }).catch(() => undefined);
  if (res && !res.ok && pending.link) {
    const why = ((await res.json().catch(() => ({}))) as { error_description?: string }).error_description;
    write(PENDING_KEY, undefined);
    throw new Error(why === 'already_linked' ? ALREADY_LINKED : SIGN_IN_EXPIRED);
  }
  if (!res?.ok) throw new Error(res ? SIGN_IN_EXPIRED : 'Mojo Up AI Cloud cannot be reached. Check the connection and try again.');
  write(PENDING_KEY, undefined);
  keep((await res.json()) as CloudTokenResponse);
  return safeReturn(pending.returnTo);
}

/**
 * Off to the provider; the trip ends at `/auth/callback`. With `link` (a ticket from
 * `routes.linkTicket()`), the provider's account is added to the signed-in person instead.
 */
export async function startProviderSignIn(provider: Exclude<SignInProvider, 'email'>, returnTo: string, link?: string): Promise<void> {
  const { pending, challenge } = await begin(provider, returnTo, undefined, Boolean(link));
  const q = new URLSearchParams({ provider, client: 'console', redirect_uri: redirectUri(), code_challenge: challenge, code_challenge_method: 'S256', state: pending.state, ...(link ? { link } : {}) });
  location.assign(`${API_URL}/auth/v1/start?${q}`);
}

/** Asks Cloud to email a six-digit code. Answered the same whether or not the address is known. */
export async function startEmailSignIn(email: string, returnTo: string, link?: string, turnstileToken?: string): Promise<void> {
  const { pending, challenge } = await begin('email', returnTo, email, Boolean(link));
  const res = await fetch(`${API_URL}/auth/v1/email/start`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ email, client: 'console', redirect_uri: redirectUri(), code_challenge: challenge, code_challenge_method: 'S256', state: pending.state, ...(link ? { link } : {}), ...(turnstileToken ? { turnstileToken } : {}) }),
  }).catch(() => undefined);
  if (!res) throw new Error('Mojo Up AI Cloud cannot be reached. Check the connection and try again.');
  if (res.status === 429) throw new Error('Too many codes for this address. Try again in an hour.');
  // Sign-up checks (a blocked email service, the person check) answer in words to show as they are.
  const refusal = res.ok ? undefined : ((await res.json().catch(() => ({}))) as { error?: string; error_description?: string });
  if (refusal?.error && SIGN_UP_REFUSALS.has(refusal.error) && refusal.error_description) throw new Error(refusal.error_description);
  if (!res.ok) throw new Error('That address does not look right.');
}

/** Sends the code that confirms an address for the provider sign-in waiting on it (`ConfirmEmailNeeded`). */
export async function startEmailConfirm(email: string): Promise<void> {
  const pending = read<Pending>(PENDING_KEY);
  if (!pending?.confirm) throw new Error(SIGN_IN_EXPIRED);
  // The same PKCE pair as the provider trip: the ticket answers only to the challenge it was made for.
  const challenge = base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(pending.verifier))));
  const res = await fetch(`${API_URL}/auth/v1/email/start`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ email, confirm: pending.confirm, client: 'console', redirect_uri: redirectUri(), code_challenge: challenge, code_challenge_method: 'S256', state: pending.state }),
  }).catch(() => undefined);
  if (!res) throw new Error('Mojo Up AI Cloud cannot be reached. Check the connection and try again.');
  if (res.status === 429) throw new Error('Too many codes for this address. Try again in an hour.');
  const refusal = res.ok ? undefined : ((await res.json().catch(() => ({}))) as { error?: string; error_description?: string });
  if (refusal?.error_description === 'confirm_expired') throw new Error(SIGN_IN_EXPIRED);
  if (refusal?.error && SIGN_UP_REFUSALS.has(refusal.error) && refusal.error_description) throw new Error(refusal.error_description);
  if (!res.ok) throw new Error('That address does not look right.');
  write(PENDING_KEY, { ...pending, email });
}

/** The six digits, then the one-time code they buy, then the tokens. Returns where to go next. */
export async function verifyEmailCode(email: string, code: string): Promise<string> {
  const pending = read<Pending>(PENDING_KEY);
  if (!pending || (pending.provider !== 'email' && !pending.confirm) || pending.email !== email) throw new Error(SIGN_IN_EXPIRED);
  const res = await fetch(`${API_URL}/auth/v1/email/verify`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ email, code, state: pending.state }),
  }).catch(() => undefined);
  if (!res) throw new Error('Mojo Up AI Cloud cannot be reached. Check the connection and try again.');
  const body = (await res.json().catch(() => ({}))) as { code?: string; state?: string; error?: string; error_description?: string };
  if (res.status === 409) throw new Error(ALREADY_LINKED);
  if (body.error && SIGN_UP_REFUSALS.has(body.error) && body.error_description) throw new Error(body.error_description);
  if (!res.ok) throw new Error(/expired/i.test(body.error_description ?? '') ? SIGN_IN_EXPIRED : CODE_NOT_RIGHT);
  if (!body.code || body.state !== pending.state) throw new Error(SIGN_IN_EXPIRED);
  return exchange(body.code, pending);
}

const finishing = new Map<string, Promise<string>>();

/**
 * The provider's return trip (`/auth/callback?code&state` or `?error&state`): check the state is
 * the one this tab sent, then exchange. Kept per code, so a second render never spends it twice.
 */
export function finishProviderSignIn(params: URLSearchParams): Promise<string> {
  const key = params.get('code') ?? params.get('confirm') ?? params.get('error') ?? '';
  let p = finishing.get(key);
  if (!p) {
    p = (async () => {
      const pending = read<Pending>(PENDING_KEY);
      const error = params.get('error');
      if (!pending || params.get('state') !== pending.state) throw new Error(SIGN_IN_EXPIRED);
      if (params.get('error_description') === 'already_linked') throw new Error(ALREADY_LINKED);
      const refused = SIGN_UP_REFUSAL_TEXT[params.get('error_description') ?? ''];
      if (refused) throw new Error(refused);
      if (params.get('error_description') === 'link_expired') throw new Error('Adding that sign-in method took too long. Try again from Settings.');
      if (error === 'access_denied') throw new Error('The sign-in was cancelled. Choose a way to sign in to try again.');
      if (error) throw new Error('The sign-in did not finish. Try again, or choose another way to sign in.');
      const confirm = params.get('confirm');
      if (confirm && !pending.link) {
        write(PENDING_KEY, { ...pending, confirm });
        throw new ConfirmEmailNeeded(pending.provider, params.get('email') ?? undefined);
      }
      const code = params.get('code');
      if (!code) throw new Error(SIGN_IN_EXPIRED);
      return exchange(code, pending);
    })();
    finishing.set(key, p);
  }
  return p;
}

/** Where a sign-in that has not finished was heading (the callback page's way back on an error). */
export const pendingReturn = (): string => safeReturn(read<Pending>(PENDING_KEY)?.returnTo);
